import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo
} from "react";
import { connect } from "react-redux";
import { Map as LeafletMap, TileLayer } from "react-leaflet";
import Markers from "./Markers";
import L from "leaflet";
import { Box, makeStyles, withTheme } from "@material-ui/core";
import "./map.scss";
import {
  fetchRoutes,
  setHoverStop,
  setLayersHidden,
  setMapBounds,
  setMapZoom,
  setPinpointDriver,
  setMapCenter
} from "../../store/actions/map";
import "@elfalem/leaflet-curve/";
import {
  toggleDrawer,
  scrollToStop,
  setStopToScrollId
} from "../../store/actions/common";
import { setExpandedRoutes } from "../../store/actions/project";
import {
  isNotEmptyRoute,
  mergeReloadStopsToMainRoute,
  remCalc
} from "../../utils/helpers";
import { createClusterCustomIcon } from "../icons/createClusterIcon";
import { CLUSTERING_MAX_ZOOM, TILES_URLS } from "../data/constants";
import DriverMarkers from "./DriverMarkers";
import PinpointDriverMarker from "./PinpointDriverMarker";
import MarkersGroup from "./MarkersGroup";
import Controls from "./Controls";
import MarkerClusterGroup from "./MarkerClusterGroup";
import { setExtendableId, setExtendableType } from "../../store/actions/edit";
import UnsavedChangesDialog from "../dialogs/UnsavedChangesDialog";
import countryLocations from "../data/locations.json";
import Polylines from "./Polylines";
import HoverMarker from "./HoverMarker";
import { path, pathEdit } from "./MarkerWrapper";
import { store } from "../../";

const useStyles = makeStyles(theme => ({
  mapAttribution: {
    fontSize: remCalc(11),
    backgroundColor: "rgba(255, 255, 255, 0.7)",

    "& a": {
      color: theme.palette.primary.main,
      textDecoration: "none"
    }
  }
}));

function Map({
  setMapCenter,
  solution,
  projectId,
  services,
  setMapZoom,
  editId,
  setMapBounds,
  fetchRoutes,
  selectedVehicles,
  drawerOpen,
  dragSelection,
  refitHash,
  hiddenRoutes,
  expandedRoutes,
  markersClustered,
  publishedOnce,
  setHoverStop,
  tilesDark,
  emptyRoutesVisible,
  toggleDrawer,
  organisation,
  setPinpointDriver,
  pinpointDriver,
  setExtendableType,
  setExtendableId,
  rightDrawerOpen,
  saveDisabled,
  scrollToStop,
  setStopToScrollId,
  setExpandedRoutes,
  isReloadAllowed,
  plan,
  showingRouteId
}) {
  const classes = useStyles();

  const [organisationLoaded, setOrganisationLoaded] = useState(false);

  useEffect(() => {
    if (organisation && !organisationLoaded) {
      const coordinates = countryLocations[organisation.country];
      if (coordinates) {
        const { sw_lat, sw_lng, ne_lat, ne_lng } = coordinates;
        console.log(sw_lat, sw_lng, ne_lat, ne_lng);
        mapRef.current.leafletElement.fitBounds(
          [
            [sw_lat, sw_lng],
            [ne_lat, ne_lng]
          ],
          {
            paddingTopLeft: projectId && drawerOpen ? [448, 0] : [0, 0],
            paddingBottomRight: rightDrawerOpen ? [280, 0] : [56, 0]
          }
        );
      }
      setOrganisationLoaded(true);
    }
    return () => {
      organisationLoaded && setOrganisationLoaded(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisation, projectId]);

  // Marker click
  const handleClick = useCallback(
    (id, type, status) => {
      const expandedRoutes = store.getState().common.expandedRoutes;

      if (type !== "start" && type !== "end") {
        toggleDrawer(true);
        setExtendableId(id);
        setExtendableType("stops", { status });

        // Scroll to and expand stops route
        const route = solution?.routes.find(el =>
          el.stops.map(el => el.id).includes(id)
        );

        if (route) {
          if (expandedRoutes.includes(route.id)) {
            scrollToStop(id);
          } else {
            setExpandedRoutes([...expandedRoutes, route.id]);
            setStopToScrollId(id);
          }
        }
      }
    },
    [toggleDrawer, setExtendableId, setExtendableType, solution]
  );

  const [changesDialog, setChangesDialog] = useState({
    visible: false,
    id: null
  });

  const saveDisabledRef = useRef(saveDisabled);

  useEffect(() => {
    saveDisabledRef.current = saveDisabled;
  }, [saveDisabled]);

  useEffect(() => {
    if (!editId && pathEdit) {
      mapRef.current.leafletElement.removeLayer(pathEdit);
    }
  }, [editId]);

  const warnClick = useCallback(
    (id, type, status) => {
      if (type !== "start" && type !== "end" && id !== editId) {
        if (!saveDisabledRef.current) {
          setChangesDialog({ visible: true, id, type, status });
        } else {
          handleClick(id, type, status);
        }
      }
    },

    [editId, setChangesDialog, handleClick]
  );

  // Routes that need to merge when we have backhaul or reload
  // IN 1.15 version when we have reloads our single route that have reload is separated by subroutes we get this from API in map we don't want show them as separate routes hence we just merge the subroutes in one route.
  const fullRoutes = useMemo(() => {
    if (solution) {
      if (isReloadAllowed) {
        return mergeReloadStopsToMainRoute(solution.routes);
      } else {
        return solution.routes;
      }
    } else {
      return [];
    }
  }, [solution, isReloadAllowed]);

  // Visible polylines
  const visibleRoutes = solution
    ? fullRoutes
        .filter(route => {
          let vehicle_id = route.vehicle_id;
          const _index = route.vehicle_id.indexOf("_");
          if (_index !== -1) {
            vehicle_id = vehicle_id.slice(0, _index);
          }
          return (
            solution.showAll ||
            (selectedVehicles.includes(vehicle_id) &&
              route.selected &&
              route.stops.length > 2)
          );
        })
        .map(route => route.id)
    : [];

  // Save map bounds to store
  const mapRef = useRef();
  const clusterPaneRef = useRef();

  const mapMoveEnd = () => {
    setMapCenter(mapRef.current.leafletElement.getCenter());
    setMapBounds(mapRef.current.leafletElement.getBounds());
  };

  useEffect(() => {
    if (organisationLoaded) {
      setMapCenter(mapRef.current.leafletElement.getCenter());
      setMapBounds(mapRef.current.leafletElement.getBounds());
    }
  }, [setMapBounds, setMapCenter, organisationLoaded]);

  useEffect(() => {
    refitServicesBounds();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refitHash]);

  useEffect(() => {
    if (solution && fullRoutes.length) {
      fetchRoutes(fullRoutes);
    }
  }, [solution, fetchRoutes]);

  useEffect(() => {
    if (solution && solution.showAll) {
      const latLngs = fullRoutes.reduce((bounds, route) => {
        return [
          ...bounds,
          route.stops
            .filter(el => el.address)
            .map(el => window.L.latLng(el.address.lat, el.address.lng))
        ];
      }, []);

      if (latLngs.length) {
        let bounds = window.L.latLngBounds(latLngs);

        const northEastLng = bounds._northEast.lng;

        bounds._northEast.lng = northEastLng;

        mapRef.current.leafletElement.fitBounds(bounds, {
          paddingTopLeft: projectId && drawerOpen ? [448, 0] : [0, 0],
          paddingBottomRight: rightDrawerOpen ? [280, 0] : [56, 0]
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solution && solution.showAll]);

  // Click "pinpointDriver" listener (remove on click outside)
  useEffect(() => {
    const clickListener = e => {
      if (
        pinpointDriver &&
        !e.target.classList.contains("pinpoint-anchor") &&
        !e.target.classList.contains("pinpoint-marker") &&
        !e.target.classList.contains("leaflet-container")
      ) {
        setPinpointDriver(null);
      }
    };

    document.addEventListener("click", clickListener);

    return () => {
      document.removeEventListener("click", clickListener);
    };
  }, [pinpointDriver, setPinpointDriver]);

  // Refit map bounds to all services
  const refitServicesBounds = () => {
    const latLngs = services
      .filter(el => el.address)
      .map(el => window.L.latLng(el.address.lat, el.address.lng));

    if (latLngs.length) {
      let bounds = window.L.latLngBounds(latLngs);

      const northEastLng = bounds._northEast.lng;

      // bounds = bounds.pad(1);
      bounds._northEast.lng = northEastLng;

      mapRef.current.leafletElement.fitBounds(bounds, {
        paddingTopLeft: projectId && drawerOpen ? [448, 0] : [0, 0],
        paddingBottomRight: rightDrawerOpen ? [280, 0] : [56, 0]
      });
    }
  };

  // Refit map bounds on services change
  const [prevServices, setPrevServices] = useState(services);

  const servicesChanged = (services, prevServices) => {
    const servicesIds = services.map(el => el.id);
    const prevServicesIds = prevServices.map(el => el.id);

    const changed = !(
      servicesIds.every(id => prevServicesIds.includes(id)) &&
      servicesIds.length === prevServicesIds.length
    );

    if (changed) setPrevServices(services);

    return changed;
  };

  const setBoundsToCoord = (
    lat,
    lng,
    leftOffset = 0.001,
    zoomOffset = 0.0015
  ) => {
    if (!isNaN(lat + lng)) {
      const latLngs = [
        window.L.latLng(lat, lng + zoomOffset - leftOffset),
        window.L.latLng(lat, lng - zoomOffset - leftOffset),
        window.L.latLng(lat + zoomOffset, lng - leftOffset),
        window.L.latLng(lat - zoomOffset, lng - leftOffset)
      ];

      let bounds = window.L.latLngBounds(latLngs);
      mapRef.current.leafletElement.fitBounds(bounds, { animate: false });
    }
  };

  useEffect(() => {
    if (servicesChanged(services, prevServices)) {
      refitServicesBounds();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services]);

  // Refit map bounds on editId change
  useEffect(() => {
    const stop = services.find(el => el.id === editId);

    if (stop && stop.address) {
      // On the small screens < 1366px we had an issue when user clicks on stop and drawer opened after that stop cluster is not visible .
      setBoundsToCoord(
        stop.address.lat,
        stop.address.lng - (window.innerWidth < 1500 ? 0.003 : 0)
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);

  // Refit map bounds on dragSelection change
  // useEffect(() => {
  //   const { list } = dragSelection;

  //   if (list.length === 1) {
  //     const stop = services.find(el => el.id === list[list.length - 1]);

  //     if (stop && stop.address) {
  //       setBoundsToCoord(stop.address.lat, stop.address.lng);
  //     }
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [dragSelection]);

  // Refit map bounds on pinpoint change
  useEffect(() => {
    const stop = services.find(el => el.id === editId);

    if (
      pinpointDriver &&
      stop &&
      pinpointDriver.finishAddress.lat &&
      pinpointDriver.finishAddress.lng
    ) {
      const finish = {
        lat: Number(pinpointDriver.finishAddress.lat),
        lng: Number(pinpointDriver.finishAddress.lng)
      };

      const val = Math.max(
        Math.abs(finish.lat - stop.address.lat),
        Math.abs(finish.lng - stop.address.lng)
      );

      setBoundsToCoord(
        (finish.lat + stop.address.lat) / 2,
        (finish.lng + stop.address.lng) / 2,
        val,
        val / 1.2
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId, pinpointDriver]);

  useEffect(() => {
    if (mapRef.current && !clusterPaneRef.current) {
      clusterPaneRef.current =
        mapRef.current.leafletElement.createPane("cluster");
      mapRef.current.leafletElement.createPane("hovered");
      mapRef.current.leafletElement.createPane("snapToRoad");
      const minZoom = mapRef.current.leafletElement.getBoundsZoom(
        [
          [-90, -180],
          [90, 180]
        ],
        true
      );
      mapRef.current.leafletElement.setMinZoom(minZoom);
    }
  }, []);

  return (
    <Box
      position="absolute"
      top={0}
      right={0}
      bottom={0}
      left={0}
      zIndex={0}
      height="100vh"
    >
      {/* Map */}
      <LeafletMap
        ref={mapRef}
        onMoveend={mapMoveEnd}
        center={[52.20221513, 5.61392784]}
        zoom={7}
        maxZoom={20}
        attributionControl={false}
        zoomControl={false}
        onZoomEnd={e => {
          setMapZoom(e.target._zoom);
          if (
            path &&
            mapRef.current &&
            mapRef.current.leafletElement &&
            e.target._zoom <= CLUSTERING_MAX_ZOOM
          ) {
            mapRef.current.leafletElement.removeLayer(path);
            setHoverStop(null);
          }
        }}
        doubleClickZoom={true}
        scrollWheelZoom={true}
        dragging={true}
        worldCopyJump={true}
        maxBounds={[
          [-90, -Infinity],
          [90, Infinity]
        ]}
        maxBoundsViscosity={1}
        animate={true}
        easeLinearity={0.35}
        renderer={L.canvas()}
      >
        {/* Markers */}
        {clusterPaneRef.current &&
          (!markersClustered ? (
            <MarkersGroup
              mapRef={mapRef}
              warnClick={warnClick}
              visibleRoutes={visibleRoutes}
              fullRoutes={fullRoutes}
            />
          ) : (
            <MarkerClusterGroup
              iconCreateFunction={createClusterCustomIcon}
              disableClusteringAtZoom={CLUSTERING_MAX_ZOOM}
              clusterPane={clusterPaneRef.current}
            >
              <MarkersGroup
                mapRef={mapRef}
                warnClick={warnClick}
                visibleRoutes={visibleRoutes}
                fullRoutes={fullRoutes}
              />
            </MarkerClusterGroup>
          ))}
        {/* Markers for expanded route (outside cluster) */}
        {solution &&
          fullRoutes &&
          fullRoutes
            .filter(route => route.id === showingRouteId)
            .map(route => {
              if (isNotEmptyRoute(route)) {
                const index = fullRoutes.findIndex(
                  route => route.id === showingRouteId
                );
                return (
                  <Markers
                    key={route.id}
                    routeIndex={index}
                    markers={fullRoutes[index].stops}
                    routeStatus={fullRoutes[index].status}
                    endLocationDefined={fullRoutes[index].end_location_defined}
                    mapRef={mapRef}
                    warnClick={warnClick}
                    routeId={route.id}
                    index={fullRoutes[index].index}
                    isRouteExpanded={true}
                  />
                );
              }
              return null;
            })}
        {/* Hovered stop marker (outside cluster) */}
        <HoverMarker mapRef={mapRef} fullRoutes={fullRoutes} />
        {/* Pinpoint Driver */}
        {pinpointDriver && <PinpointDriverMarker />}
        {/* Driver markers */}
        {publishedOnce && (
          <DriverMarkers mapRef={mapRef} setBoundsToCoord={setBoundsToCoord} />
        )}
        {/* Polylines */}
        {solution && fullRoutes && (
          <Polylines
            mapRef={mapRef}
            visibleRoutes={visibleRoutes}
            fullRoutes={fullRoutes}
          />
        )}
        {/* Stadia tiles */}
        {plan.default_map_tile_provider && (
          <TileLayer
            maxZoom={L.Browser.retina ? 21 : 20}
            maxNativeZoom={L.Browser.retina ? 19 : 20}
            url={
              tilesDark
                ? TILES_URLS[`${plan.default_map_tile_provider}_DARK`]
                : TILES_URLS[`${plan.default_map_tile_provider}`]
            }
            detectRetina
            onLoad={() => {
              setMapCenter(mapRef.current.leafletElement.getCenter());
              setMapBounds(mapRef.current.leafletElement.getBounds());
            }}
          />
        )}
        {/* Attribution */}
        <Box
          position="absolute"
          bottom={0}
          right={rightDrawerOpen ? 280 : 56}
          zIndex={1000}
          px={0.75}
          className={classes.mapAttribution}
        >
          <a
            href="https://leafletjs.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            Leaflet
          </a>{" "}
          | &copy; {new Date().getFullYear()} <a href="/">Routetitan</a>,
          {["stadia", "stadiamaps"].includes(
            plan.default_map_tile_provider?.toLowerCase()
          ) && (
            <>
              &copy; <a href="https://stadiamaps.com/">Stadia Maps</a>,
            </>
          )}{" "}
          map data &copy; OpenStreetMap contributors
        </Box>
      </LeafletMap>

      {/* Custom Controls */}
      <Controls mapRef={mapRef} refitServicesBounds={refitServicesBounds} />
      <UnsavedChangesDialog
        open={changesDialog.visible}
        key={-1}
        handleClose={() => setChangesDialog({ visible: false })}
        action={() =>
          handleClick(
            changesDialog.id,
            changesDialog.type,
            changesDialog.status
          )
        }
      />
    </Box>
  );
}

function mapStateToProps(state) {
  return {
    projectId: state.project.id,
    projectStatus: state.project.status,
    publishedOnce: state.project.published_once,
    solution: state.project.solution,
    services: state.project.vrp.stops,
    editId: state.extendable.type === "stops" && state.extendable.id,
    markersClustered: state.map.markersClustered,
    selectedVehicles: state.project.selectedVehicles,
    expandedRoutes: state.common.expandedRoutes,
    hiddenRoutes: state.map.hiddenRoutes,
    tilesDark: state.map.tilesDark,
    dragSelection: state.common.dragSelection,
    pinpointDriver: state.map.pinpointDriver,
    emptyRoutesVisible: state.common.emptyRoutesVisible,
    saveDisabled: state.extendable.saveDisabled,
    organisation:
      state.auth.userData.organisationSite &&
      state.auth.userData.organisationSite.organisation,
    rightDrawerOpen: state.account.accountDrawerOpen,
    drawerOpen: state.common.drawerOpen,
    plan:
      (state.auth.userData.organisationSite &&
        state.auth.userData.organisationSite.organisation.plan) ||
      {},
    refitHash: state.map.refitHash,
    isReloadAllowed: state.project.vrp.configuration.allow_reload,
    showingRouteId: state.project.showingRouteId
  };
}

export default connect(mapStateToProps, {
  setMapCenter,
  setMapBounds,
  fetchRoutes,
  setLayersHidden,
  setExtendableId,
  setPinpointDriver,
  setExtendableType,
  toggleDrawer,
  setHoverStop,
  setMapZoom,
  scrollToStop,
  setStopToScrollId,
  setExpandedRoutes
})(withTheme(Map));
